import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { FetchPolicy, gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { Meeting, MeetingInput } from '@app/core/model/entities/meeting/meeting';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, Observable, Subject } from 'rxjs';
import { map, reduce, tap } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class MeetingsService {

  private sidePanelToggle = new Subject<Meeting | null>();
  private meetingAdded = new Subject<Meeting>();
  private meetingUpdated = new Subject<Meeting>();
  private meetingsDeleted = new Subject<Meeting[]>();

  private readonly meetingInfoGraphqlFragment = gql`
    fragment MeetingInfo on Meeting {
      id
      name
      identifier
      organizationId
      properties
      computedProperties
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
    }
  `;

  private appManager = inject(AppManager);
  private activationEndService = inject(ActivationEndService);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private appConfig = inject(AppConfig);

  /**
   * Emits data for the side panel to display or null whenever the side panel is closed.
   * @return Observable emitting the meeting to open the side panel with or null if the sidePanel is closing.
   */
  public get sidePanelToggle$(): Observable<Meeting | null> {
    return this.sidePanelToggle.asObservable();
  }

  /**
   * Emits the new Meeting after it was created.
   * @return Observable emitting the new Meeting.
   */
  public get meetingAdded$(): Observable<Meeting> {
    return this.meetingAdded.asObservable();
  }

  /**
   * Emits the deleted Meetings after they were deleted.
   * @return Observable emitting the deleted Meeting.
   */
  public get meetingsDeleted$(): Observable<Meeting[]> {
    return this.meetingsDeleted.asObservable();
  }

  /**
   * Emits the updated Meeting after it was updated.
   * @return Observable emitting the updated meeting.
   */
  public get meetingUpdated$(): Observable<Meeting> {
    return this.meetingUpdated.asObservable();
  }

  /**
   * Make an API request to fetch a Meeting.
   * @param id ID of the Meeting.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Observable emitting the Meeting.
   */
  public loadMeeting(id: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Meeting> {
    const query = gql`
      query Meeting($id: String!) {
        meeting(id: $id) {
          ...MeetingInfo
        }
      } ${this.meetingInfoGraphqlFragment}
    `;
    const variables = {id};

    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Meeting, data['meeting'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Loads a meeting page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch meetings based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the meetings.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Meeting objects, each with additional user information.
   */
  private loadMeetingsPage(selectParams: SelectParams): Observable<Meeting[]> {
    const query = gql`
      query Meetings($organizationId: String!, $selectParams: SelectParams!) {
        meetings(organizationId: $organizationId, selectParams: $selectParams) {
          ...MeetingInfo
        }
      } ${this.meetingInfoGraphqlFragment}
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Meeting, data['meetings'] as Meeting[])));
  }

  /**
   * Loads all meeting pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch meetings based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   * It also fetches information about users who created and last updated the meetings.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Meeting objects, each with additional user information.
   */
  public loadAllMeetings(selectParams: SelectParams): Observable<Meeting[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadMeetingsPage(params)
      .pipe(
        expand((meetings, i) => {
          if (meetings.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadMeetingsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Meeting[])
      );
  }

  /**
   * Create a new Meeting with the provided data.
   * @param meetingInput Meeting data.
   * @return An Observable emitting the created Meeting.
   */
  public createMeeting(meetingInput: MeetingInput): Observable<Meeting> {
    const mutation = gql`
      mutation CreateMeeting($meetingInput: CreateMeetingInput!) {
        createMeeting(meetingInput: $meetingInput) {
          ...MeetingInfo
        }
      }
      ${this.meetingInfoGraphqlFragment}
    `;
    const variables = {
      meetingInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...meetingInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Meeting, data['createMeeting'])));
  }

  /**
   * Call the API to update a Meeting.
   * @param meeting Meeting to update.
   * @param meetingInput Data to update the Meeting with.
   * @return An Observable emitting the updated Meeting once the update is completed.
   */
  public updateMeeting(meeting: Meeting, meetingInput: MeetingInput): Observable<Meeting> {
    const mutation = gql`
      mutation UpdateMeeting($meetingInput: UpdateMeetingInput!) {
        updateMeeting(meetingInput: $meetingInput) {
          ...MeetingInfo
        }
      }
      ${this.meetingInfoGraphqlFragment}
    `;
    const variables = {
      meetingInput: {
        id: meeting.id,
        name: meeting.name,
        ...meetingInput
      }
    };

    return this.apiService.mutate({mutation, variables}).pipe(
      map(data => plainToInstance(Meeting, data['updateMeeting'])),
      tap(meeting => this.meetingUpdated.next(meeting))
    );
  }

  /**
   * Call the API to delete Meetings.
   * @param meetings Meetings to delete.
   * @return An Observable emitting true if all Meetings have been deleted successfully, false otherwise.
   */
  public deleteMeetings(meetings: Meeting[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteMeetings($ids: [String!]!) {
        deleteMeetings(ids: $ids)
      }
    `;
    const variables = {
      ids: meetings.map(meeting => meeting.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteMeetings']),
        tap(success => success && this.meetingsDeleted.next(meetings))
      );
  }

  /**
   * Open the Meeting side panel.
   * @param meeting Meeting to display in the side panel.
   */
  public openMeetingSidePanel(meeting: Meeting): void {
    this.sidePanelToggle.next(meeting);
  }

  /**
   * Close the Meeting side panel.
   */
  public closeMeetingSidePanel(): void {
    this.sidePanelToggle.next(null);
  }

  /**
   * Navigate to the Meeting sheet.
   * @param id ID of the Meeting to navigate to.
   */
  public async navigateToMeetingSheet(id: string): Promise<void> {
    await this.router.navigate(['organization', this.appManager.currentOrganization.id, 'meetings', 'meeting-sheet', id]);
  }
}
